// Shared date-preset pieces for the forms-domain FilterDropdown categories
// (Form Templates' Published date / Last updated, Reports' Published date /
// Last entry). One preset list + the serialization helpers, so every page maps
// "Any time" → undefined and custom FROM/TO the same way.

import { FormDateFilter } from '@/api/endpoints/formsApi';
import type { FilterDropdownDateRange, FilterDropdownOption } from '@/components/common';

/** UI-only id for the "Any time" preset — it means "send nothing", so it is
 *  deliberately absent from FormDateFilter and mapped to undefined on the wire.
 *  Used as the categories' defaultOptionId (selected look while the set is
 *  empty, excluded from badge counts). */
export const DATE_ANY = 'any';

/** The six preset rows of a date category (labels resolve via the caller's t). */
export const buildDateFilterOptions = (t: (key: string) => string): FilterDropdownOption[] => [
  { id: DATE_ANY, label: t('forms_filter_date_any'), icon: 'clock' },
  { id: FormDateFilter.TODAY, label: t('text_today'), icon: 'clock' },
  { id: FormDateFilter.LAST_7_DAYS, label: t('select_last_seven_days'), icon: 'clock' },
  { id: FormDateFilter.LAST_30_DAYS, label: t('select_last_thirty_days'), icon: 'clock' },
  { id: FormDateFilter.LAST_90_DAYS, label: t('forms_filter_date_last_90'), icon: 'clock' },
  { id: FormDateFilter.CUSTOM, label: t('inspection_filter_due_custom'), icon: 'calendar' },
];

/** The picked preset of a single-select date category, or undefined for "no
 *  filter" (empty set — 'any' never lands in it, picking it clears). */
export const dateFilterOf = (
  filters: Record<string, Set<string>>,
  key: string,
): FormDateFilter | undefined => {
  const picked = filters[key].size ? [...filters[key]][0] : undefined;
  return picked && picked !== DATE_ANY ? (picked as FormDateFilter) : undefined;
};

/** One edge of a category's custom range — only sent while CUSTOM is picked. */
export const dateRangeOf = (
  ranges: Record<string, FilterDropdownDateRange>,
  key: string,
  mode: FormDateFilter | undefined,
  edge: 'start' | 'end',
): string | undefined =>
  mode === FormDateFilter.CUSTOM ? ranges[key]?.[edge] || undefined : undefined;
